/**
 * Database Schema Updater Utility
 * 
 * This utility updates the database schema by adding missing columns required
 * for consistent document display across preview and archive views.
 */

// Import the API configuration
import i18n from '../../i18n';
import { API_BASE } from './apiConfig';
import { isCloudDemoRuntime } from './publicDemoRuntime';

const su = (key, opts) => i18n.t(`common.schema_update.${key}`, opts);

/**
 * Updates the database schema with missing fields
 * @returns {Promise<Object>} Result of the schema update operation
 */
export const updateDatabaseSchema = async () => {
  try {
    if (isCloudDemoRuntime()) {
      console.log('🧪 Skipping database schema update in cloud demo (no remote SQL DB)');
      return { success: true, message: su('skipped_demo') };
    }

    // Skip schema update in Electron mode (standalone)
    const isElectron = (typeof window !== 'undefined' && window.electronAPI !== undefined) || false;
    if (isElectron) {
      console.log('🔌 Skipping database schema update in Electron mode');
      return { success: true, message: su('skipped_electron') };
    }

    console.log('Updating database schema...');
    
    // First check if API server is available
    try {
      const healthCheck = await fetch(`${API_BASE}/health`, {
        method: 'GET'
      });
    } catch (healthError) {
      console.log('⚠️ API server not reachable, skipping database schema update');
      return { success: true, message: su('api_unreachable') };
    }
    
    // Step 1: Update base document fields (partnerNumber, etc.)
    console.log('Updating base document fields...');
    const baseResponse = await fetch(`${API_BASE}/documents-fields/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'update_schema'
      })
    });
    
    if (!baseResponse.ok) {
      // If endpoint doesn't exist (404), skip gracefully
      if (baseResponse.status === 404) {
        console.log('⚠️ Schema update endpoint not available, skipping database schema update');
        return { success: true, message: su('endpoint_unavailable') };
      }
      throw new Error(su('base_failed', { status: baseResponse.status, statusText: baseResponse.statusText }));
    }
    
    const baseResult = await baseResponse.json();
    console.log('Base schema update result:', baseResult);
    
    // Step 2: Update document detail fields (payment terms, delivery date, etc.)
    console.log('Updating document detail fields...');
    const detailResponse = await fetch(`${API_BASE}/documents-details/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'update_schema'
      })
    });
    
    if (!detailResponse.ok) {
      // If endpoint doesn't exist (404), skip gracefully
      if (detailResponse.status === 404) {
        console.log('⚠️ Detail schema update endpoint not available, using base result only');
        return { 
          success: true, 
          message: su('partial_update'), 
          details: { baseFields: baseResult, detailFields: null }
        };
      }
      throw new Error(su('detail_failed', { status: detailResponse.status, statusText: detailResponse.statusText }));
    }
    
    const detailResult = await detailResponse.json();
    console.log('Detail schema update result:', detailResult);
    
    return {
      success: true,
      message: su('success'),
      details: {
        baseFields: baseResult,
        detailFields: detailResult
      }
    };
  } catch (error) {
    console.error('Error updating database schema:', error);
    return {
      success: false,
      message: su('failed', { message: error.message }),
      error
    };
  }
};

/**
 * Check if we should run the schema update
 * This is meant to be called early in the app initialization process
 */
export const checkAndUpdateSchema = async () => {
  try {
    // Check if we've already updated the schema in this session
    const schemaUpdated = localStorage.getItem('qawaem_schema_updated');
    if (schemaUpdated) {
      console.log('Database schema already updated in this session');
      return;
    }
    
    // Run the schema update
    const result = await updateDatabaseSchema();
    
    if (result.success) {
      // Mark the schema as updated for this session
      localStorage.setItem('qawaem_schema_updated', 'true');
      console.log('Schema update completed successfully');
    } else {
      console.error('Schema update failed:', result.message);
    }
  } catch (error) {
    console.error('Error checking/updating schema:', error);
  }
};

export default {
  updateDatabaseSchema,
  checkAndUpdateSchema
};

